import React, { useEffect, useRef, useState, useCallback } from 'react';
import { isFemaleName } from '../../../services/voiceService';

/**
 * Generates dynamic SVG avatar matching Trainova's visual identity
 */
export function getPersonaAvatarSVG(personaName = 'AI Persona'): string {
  const name = (personaName || 'AI Persona').trim();
  const parts = name.split(/\s+/);
  const initials = parts.length > 1
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hues = [
    { start: '#1E6F7A', end: '#0B2F45', fill: '#0E7C86', accent: '#FFB547' },
    { start: '#22607F', end: '#0B2440', fill: '#0E7C86', accent: '#38BDF8' },
    { start: '#4C1D95', end: '#1E1B4B', fill: '#6D28D9', accent: '#C084FC' },
    { start: '#831843', end: '#500724', fill: '#9D174D', accent: '#F472B6' },
    { start: '#14532D', end: '#052E16', fill: '#15803D', accent: '#4ADE80' },
    { start: '#7C2D12', end: '#451A03', fill: '#9A3412', accent: '#FB923C' }
  ];
  const theme = hues[Math.abs(hash) % hues.length];
  const gradId = `personaGrad_${Math.abs(hash)}`;

  return `
<svg class="lv-av" viewBox="0 0 600 520" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg" style="position: absolute; inset: 0; width: 100%; height: 100%;">
  <defs>
    <radialGradient id="${gradId}" cx="50%" cy="36%" r="72%">
      <stop offset="0%" fill="${theme.start}"/>
      <stop offset="100%" fill="${theme.end}"/>
    </radialGradient>
  </defs>
  <rect width="600" height="520" fill="url(#${gradId})"/>
  <g opacity=".08" fill="#fff">
    <circle cx="90" cy="90" r="46"/>
    <circle cx="520" cy="130" r="30"/>
    <rect x="440" y="300" width="120" height="14" rx="7"/>
    <rect x="40" y="330" width="90" height="14" rx="7"/>
  </g>
  <path d="M110 520c10-112 88-172 190-172s180 60 190 172z" fill="${theme.fill}"/>
  <path d="M110 520c6-40 20-72 44-98l14 98z" fill="${theme.accent}" opacity=".95"/>
  <path d="M490 520c-6-40-20-72-44-98l-14 98z" fill="${theme.accent}" opacity=".95"/>
  <circle cx="300" cy="230" r="90" fill="#E2E8F0" opacity=".12"/>
  <text x="300" y="265" text-anchor="middle" fill="#FFFFFF" font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" font-size="96" font-weight="700" letter-spacing="2">${initials}</text>
</svg>`;
}

export interface SpeechControlledAvatarProps {
  avatarUrl?: string | null;
  avatarType?: string | null;
  isSpeaking: boolean;
  personaName?: string;
  genderPreference?: string;
  className?: string;
  style?: React.CSSProperties;
  renderFallback?: () => React.ReactNode;
}

export const SpeechControlledAvatar: React.FC<SpeechControlledAvatarProps> = ({
  avatarUrl,
  avatarType = 'gif',
  isSpeaking,
  personaName = 'AI Persona',
  genderPreference,
  className = 'lv-av-img',
  style,
  renderFallback,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [hasCanvasFrame, setHasCanvasFrame] = useState(false);
  const [imgError, setImgError] = useState(false);

  const isFemale = genderPreference === 'female' || (genderPreference !== 'male' && isFemaleName(personaName));
  const defaultPortrait = isFemale
    ? 'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=600&q=80'
    : 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=600&q=80';

  const rawClean = (avatarUrl || '').trim();
  const cleanUrl = rawClean && rawClean !== '/avatars/default.svg'
    ? rawClean
    : defaultPortrait;

  const isVideo =
    avatarType === 'video' ||
    (cleanUrl &&
      (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(cleanUrl) ||
        cleanUrl.startsWith('data:video/')));

  // Capture still frame onto canvas from the loaded image
  const captureStillFrame = useCallback((img: HTMLImageElement) => {
    if (!canvasRef.current || !img) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    try {
      const naturalWidth = img.naturalWidth || img.width || 400;
      const naturalHeight = img.naturalHeight || img.height || 400;

      if (naturalWidth > 0 && naturalHeight > 0) {
        canvas.width = naturalWidth;
        canvas.height = naturalHeight;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, naturalWidth, naturalHeight);
        setHasCanvasFrame(true);
      }
    } catch {
      // If tainted due to CORS, canvas gracefully falls back to direct image display
      setHasCanvasFrame(false);
    }
  }, []);

  // Pre-fetch and draw static first frame offscreen to freeze idle frame if CORS allows
  useEffect(() => {
    if (!cleanUrl || isVideo) return;
    const offscreenImg = new Image();
    try {
      if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
        offscreenImg.crossOrigin = 'anonymous';
      }
      offscreenImg.src = cleanUrl;
      offscreenImg.onload = () => {
        captureStillFrame(offscreenImg);
      };
      offscreenImg.onerror = () => {
        // Silently fall back to normal img display if offscreen CORS fails
      };
    } catch {
      // Ignore offscreen errors
    }
  }, [cleanUrl, isVideo, captureStillFrame]);

  // Handle Image onLoad
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImgError(false);
    captureStillFrame(img);
  };

  // Check if img is already loaded from browser cache
  useEffect(() => {
    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      captureStillFrame(imgRef.current);
    }
  }, [cleanUrl, captureStillFrame]);

  // Video playback sync with isSpeaking state
  useEffect(() => {
    if (!isVideo || !videoRef.current) return;
    const video = videoRef.current;

    if (isSpeaking) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Autoplay policy fallback
        });
      }
    } else {
      video.pause();
    }
  }, [isSpeaking, isVideo]);

  // When avatarUrl changes, reset frame & error state
  useEffect(() => {
    setHasCanvasFrame(false);
    setImgError(false);
  }, [cleanUrl]);

  // If no avatarUrl or image load error, render custom fallback or default initials
  if (!cleanUrl || cleanUrl === '/avatars/default.svg' || imgError) {
    if (renderFallback) {
      return <>{renderFallback()}</>;
    }
    return (
      <div
        className={className}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          ...style,
        }}
        dangerouslySetInnerHTML={{ __html: getPersonaAvatarSVG(personaName) }}
      />
    );
  }

  // 1. VIDEO AVATAR (.mp4, .webm)
  if (isVideo) {
    return (
      <video
        ref={videoRef}
        src={cleanUrl}
        playsInline
        muted
        loop
        onError={() => setImgError(true)}
        className={className}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          ...style,
        }}
      />
    );
  }

  // 2. ANIMATED GIF / IMAGE AVATAR
  // When isSpeaking === true: show live animated GIF image element

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        boxShadow: isSpeaking ? 'inset 0 0 0 2px #2F7BFF, 0 0 14px rgba(47, 123, 255, 0.45)' : 'none',
        transition: 'box-shadow 0.2s ease',
        ...style,
      }}
    >
      <style>{`
        @keyframes trainova-eq-bar {
          0% { height: 3px; }
          100% { height: 13px; }
        }
      `}</style>

      {/* Active Image Element for live animated playback and image rendering */}
      <img
        ref={imgRef}
        src={cleanUrl}
        alt={personaName}
        onLoad={handleImageLoad}
        onError={() => setImgError(true)}
        className={`${className} lv-av-img`}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: isSpeaking || !hasCanvasFrame ? 'block' : 'none',
          opacity: isSpeaking || !hasCanvasFrame ? 1 : 0,
          transform: isSpeaking ? 'scale(1.03)' : 'scale(1)',
          transition: 'transform 0.25s ease-out, opacity 0.15s ease-in-out',
        }}
      />

      {/* Frozen Still Canvas for Idle / Non-speaking State */}
      <canvas
        ref={canvasRef}
        className="lv-av-canvas"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: !isSpeaking && hasCanvasFrame ? 'block' : 'none',
          opacity: !isSpeaking && hasCanvasFrame ? 1 : 0,
          transition: 'opacity 0.15s ease-in-out',
        }}
      />

      {/* Live Speaking Equalizer Waveform Overlay */}
      {isSpeaking && (
        <div
          style={{
            position: 'absolute',
            bottom: '4px',
            right: '4px',
            display: 'flex',
            alignItems: 'flex-end',
            gap: '2px',
            padding: '2px 4px',
            borderRadius: '4px',
            background: 'rgba(7, 29, 69, 0.85)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
            zIndex: 10,
          }}
        >
          <span style={{ width: '2px', height: '8px', background: '#38BDF8', borderRadius: '1px', animation: 'trainova-eq-bar 0.5s ease-in-out infinite alternate', animationDelay: '0s' }} />
          <span style={{ width: '2px', height: '13px', background: '#38BDF8', borderRadius: '1px', animation: 'trainova-eq-bar 0.7s ease-in-out infinite alternate', animationDelay: '0.2s' }} />
          <span style={{ width: '2px', height: '6px', background: '#38BDF8', borderRadius: '1px', animation: 'trainova-eq-bar 0.6s ease-in-out infinite alternate', animationDelay: '0.35s' }} />
        </div>
      )}
    </div>
  );
};
